import { NavLink } from 'react-router-dom';
import { CubixLockup } from '@/components/shared/CubixMark';
import {
  Activity,
  ArrowLeftRight,
  Bell,
  Droplets,
  FileBarChart,
  FileText,
  FileUp,
  FlaskConical,
  Gauge,
  LayoutDashboard,
  LogOut,
  Percent,
  Route,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Sliders,
  Spline,
  Timer,
  TrendingUp,
  UserCircle,
  Users,
  Zap,
  Layers,
  Landmark,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/lib/auth/store';
import { useCurrentUser } from '@/lib/auth/hooks';
import { useAlerts } from '@/features/alerts/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: 'ALM Analysis',
    items: [
      { to: '/alm/gaps',                   label: 'Gap analysis',     icon: TrendingUp,    permission: 'gap:read' },
      { to: '/parameters/segmentations',    label: 'Segmentations',    icon: Layers,    permission: 'gap:read' },
      { to: '/parameters/flow-models',     label: 'Flow models',      icon: Sliders,       permission: 'gap:read' },
      { to: '/parameters/affectations',    label: 'Affectations',     icon: Route,         permission: 'gap:read' },
    ],
  },
  {
    label: 'Liquidity',
    items: [
      { to: '/alm/liquidity', label: 'LCR / NSFR', icon: Droplets, permission: 'lcr:read' },
    ],
  },
  {
    label: 'IRRBB',
    items: [
      { to: '/alm/nim',       label: 'NIM',           icon: Percent,      permission: 'gap:read' },
      { to: '/alm/eve',       label: 'EVE / KRD',     icon: ShieldAlert,  permission: 'gap:read' },
      { to: '/alm/duration',  label: 'Duration / DV01', icon: Timer,      permission: 'gap:read' },
      { to: '/reporting/irrbb', label: 'BAM report',  icon: FileText,     permission: 'gap:read' },
    ],
  },
  {
    label: 'Pricing',
    items: [
      { to: '/alm/ftp',              label: 'FTP analysis', icon: ArrowLeftRight, permission: 'gap:read' },
      { to: '/parameters/yield-curves', label: 'Yield curves', icon: Spline,     permission: 'gap:read' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { to: '/reporting/gap-liquidity', label: 'Liquidity gap',  icon: TrendingUp,   permission: 'gap:read' },
      { to: '/reporting/gap-rate',      label: 'Rate gap',        icon: Activity,     permission: 'gap:read' },
      { to: '/reporting/lcr',           label: 'LCR / NSFR',     icon: FileBarChart, permission: 'lcr:read' },
      { to: '/alm/stress',              label: 'Stress tests',    icon: Zap,          permission: 'gap:read' },
      { to: '/parameters/preview',      label: 'Data preview',    icon: FlaskConical, permission: 'gap:read' },
    ],
  },
  {
    label: 'Data Ingest',
    items: [
      { to: '/data/import',               label: 'Import positions',    icon: FileUp,    permission: 'gap:read' },
      { to: '/data/upload',               label: 'Raw CSV upload',       icon: FileText,  permission: 'gap:read' },
      { to: '/data/accounting-balances',  label: 'Accounting balances',  icon: Landmark,  permission: 'gap:read' },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      { to: '/alm/alerts', label: 'Alerts', icon: Bell, permission: 'gap:read' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/settings/report-configs', label: 'Report configs',   icon: Settings2,   permission: 'gap:read' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/settings/users',          label: 'Users',           icon: Users,       permission: 'user:admin' },
      { to: '/settings/permissions',    label: 'Habilitations',   icon: ShieldCheck, permission: 'gap:read' },
      { to: '/settings/alm-parameters', label: 'ALM parameters',  icon: Gauge,       permission: 'gap:read' },
      { to: '/settings/profile',        label: 'My profile',      icon: UserCircle },
    ],
  },
];

export function Sidebar() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const logout = useAuthStore((s) => s.logout);
  const user = useCurrentUser();

  // Live unread alert count — refreshes every 60 s (same interval as AlertsPage)
  const alertQuery = useAlerts(false);
  const unreadCount = alertQuery.data?.unacknowledgedCount ?? 0;

  const visible = (item: NavItem) => !item.permission || hasPermission(item.permission);

  return (
    <aside className="flex w-60 shrink-0 flex-col bg-cx-navy">
      {/* Logo */}
      <div className="flex h-14 items-center justify-between px-4 border-b border-white/10">
        <CubixLockup markSize={22} dark={false} />
        <span className="rounded bg-white/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-white/40">
          ALM
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter(visible);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label} className="mb-1">
              <SectionLabel label={section.label} />
              {visibleItems.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  item={item}
                  badge={item.to === '/alm/alerts' && unreadCount > 0 ? unreadCount : undefined}
                />
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer — tenant + user */}
      <div className="border-t border-white/10 p-3 space-y-2">
        <div className="flex items-center gap-2 px-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-medium text-white">
            {user?.email?.slice(0, 2).toUpperCase() ?? 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] font-medium text-white/80">{user?.email ?? '—'}</p>
            <p className="truncate text-[10px] text-white/40">{user?.tenant_id ?? '—'}</p>
          </div>
          <button
            onClick={logout}
            className="text-white/40 hover:text-white/80 transition-colors"
            title="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="px-2 text-[10px] text-white/25">
          cx-web · v{import.meta.env.VITE_APP_VERSION ?? '1.28.0'} · ManTrust
        </p>
      </div>
    </aside>
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="px-5 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-widest text-white/35">
      {label}
    </p>
  );
}

function SidebarNavLink({ item, badge }: { item: NavItem; badge?: number }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'mx-2 flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[12px] font-medium',
          'transition-colors',
          isActive
            ? 'bg-white/12 text-white'
            : 'text-white/60 hover:bg-white/6 hover:text-white/90',
        )
      }
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate flex-1">{item.label}</span>
      {badge != null && badge > 0 && (
        <span className="ml-auto flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}
